/**
 * The employee-directory contract — and, deliberately, the pattern every other module's contract
 * should copy.
 *
 * `auth.contract.ts` is NOT that pattern: it is bootstrap logic for a caller who has no tenant and
 * no session yet, and its shapes (org-slug resolution, MFA challenges, token rotation) are special
 * cases that do not generalise. This file is what an ordinary, tenant-scoped, permission-gated
 * resource looks like — list with cursor pagination, get-one, create, update — and it is the one
 * to read before adding leave, attendance, expenses, or anything else in that shape.
 *
 * Three conventions worth naming explicitly, because they are easy to get subtly wrong per module:
 *
 *   1. List/detail responses are DTOs distinct from the Prisma model. `employeeSchema` below is
 *      not "Person minus the PII columns" applied ad hoc at the route — it is its own schema, so
 *      adding a column to `people` can never leak it to a client by omission.
 *   2. Every route's target for `can()` is resolvable from the DTO alone (orgId, legalEntityId,
 *      departmentId, personId, managerChain) — see ResourceTarget in @hrms/shared/permissions.
 *      A DTO that cannot supply these cannot be authorised without a second query.
 *   3. List responses use `paginatedSchema()` from common.ts, never a bare array — an unbounded
 *      `Employee[]` response is the endpoint that falls over first at 5,000 employees.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  countrySchema,
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  emailSchema,
  errorBodySchema,
  genderSchema,
  idempotencyKeySchema,
  legalEntityIdSchema,
  maritalStatusSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  phoneSchema,
  uuidSchema,
  employmentTypeSchema,
  employmentStatusSchema,
  exitTypeSchema,
  localeSchema,
  workTypeSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';
import {
  confirmProbationBodySchema,
  extendProbationBodySchema,
  extendProbationResponseSchema,
  probationDetailSchema,
  probationExtensionListSchema,
  probationExtensionSchema,
  probationSummarySchema,
} from './probation.schemas.js';
import { PROFILE_FIELD_KEYS } from '../lib/profile-fields.js';
import { customFieldValueMapSchema } from './employee-custom-fields.contract.js';
import {
  cancelTransferBodySchema,
  previewTransferQuerySchema,
  transferEmployeeBodySchema,
  transferLinkSchema,
  transferPreviewSchema,
  transferResultSchema,
} from './employee-transfer.contract.js';

/**
 * Every field optional: with one legal entity and one location (the usual case right after sign-up)
 * the API picks them, and the joining date defaults to today in that location's timezone.
 */
export const addMyEmploymentBodySchema = z.object({
  legalEntityId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  designation: z.string().trim().max(200).optional(),
  joinedOn: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .optional(),
});

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * The directory row. Never the Prisma `Person` shape directly — see convention 1 above. Notably
 * absent: every PII ciphertext/blind-index column, `dekWrapped`, and anything from `Credential`.
 * `employee.pii:read` gates a SEPARATE, smaller set of fields (DOB, PAN, Aadhaar) on the detail
 * route, not this one — the directory is what most roles see; PII is what few do.
 */
export const employeeSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  /**
   * THE PERSON id — distinct from `id`/`employmentId` above, which are both the EMPLOYMENT id (a
   * person and their job are separate rows; see CLAUDE.md invariant 4). Anything that targets a
   * PERSON-scoped resource — a leave request's `personId`, a `ResourceTarget.personId`,
   * `Goal.personId`, `Feedback.subjectPersonId`, a 1:1's participant, a recognition recipient —
   * needs THIS field, never `id`. Employment and Person are different rows with different ids that
   * happen to both be UUIDs, so sending the wrong one type-checks fine and fails only as a
   * foreign-key violation (or a 404/422) at write time — see leave.ts's own header comment for the
   * exact failure mode this field exists to prevent.
   */
  personId: uuidSchema,
  employeeCode: z.string(),

  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().nullable(),
  /**
   * The work email — the usual login identity. Null for a PHONE-ONLY employee (added from the
   * owner's contacts at sign-up, or by HR with a mobile number and no email): they sign in with
   * `phone` and cannot be emailed — notifications fall back to in-app/SMS, invites go by text.
   */
  email: emailSchema.nullable(),
  /** Alternate login identity — see loginRequestSchema's doc comment. Not PII, shown same as email. */
  username: z.string().nullable(),
  phone: phoneSchema.nullable(),
  photoUrl: z.string().url().nullable(),
  locale: localeSchema,

  legalEntityId: legalEntityIdSchema,
  departmentId: uuidSchema.nullable(),
  departmentName: z.string().nullable(),
  locationId: uuidSchema.nullable(),
  locationName: z.string().nullable(),
  designation: z.string().nullable(),
  /// Free text, matching `EmploymentAssignment.grade` and `Competency.expectedLevelByGrade`'s key
  /// convention — see the schema comment. Not an enum: grade becomes a lookup table only if it
  /// becomes first-class, same reasoning as `designation`.
  grade: z.string().nullable(),
  /**
   * The catalogue grade / cost centre on the CURRENT assignment (ADR 0022). Optional only so this
   * contract change can land before the API that fills them; once it does they are always present,
   * null when the assignment links none. `grade` above stays the free-text code, mirrored from the
   * catalogue when one is linked.
   */
  gradeId: uuidSchema.nullable().optional(),
  gradeName: z.string().nullable().optional(),
  costCentreId: uuidSchema.nullable().optional(),
  costCentreCode: z.string().nullable().optional(),
  costCentreName: z.string().nullable().optional(),
  /** The business unit on the CURRENT assignment (ORG-6). Optional so the contract can land first. */
  businessUnitId: uuidSchema.nullable().optional(),
  businessUnitCode: z.string().nullable().optional(),
  businessUnitName: z.string().nullable().optional(),
  shiftId: uuidSchema.nullable(),
  shiftName: z.string().nullable(),

  /** The current assignment's manager, by EMPLOYMENT id — reporting is a property of the job. */
  managerEmploymentId: uuidSchema.nullable(),
  managerName: z.string().nullable(),

  employmentType: employmentTypeSchema,
  employmentStatus: employmentStatusSchema,
  joinedOn: dateOnlySchema,
  /** When probation ends (null once confirmed, or when none was set). Optional so routes that
   * return this shape without it stay valid; `list` and `get` fill it. */
  probationEndOn: dateOnlySchema.nullable().optional(),
  /** HR's on/off switch — false excludes the person from nightly attendance. See Employment.isActive. */
  isActive: z.boolean(),
  /** True = marked present automatically on working days with no punches. See Employment.autoAttendance. */
  autoAttendance: z.boolean(),
  /**
   * Sign-in state, on the list and the detail: see SIGN_IN_STATUSES. Absent where the API did not
   * compute it (older callers, other modules' stand-ins).
   */
  signInStatus: z.enum(['active', 'invited', 'link_expired']).optional(),
  /** True once HR has deleted (archived) the person from the list. See Employment.archivedAt. */
  isArchived: z.boolean(),
  /**
   * Where this job came from, when it began as a transfer between legal entities: the OLD
   * employment, its company and the day the move took effect. Optional so the contract can land
   * before the API that fills it; once it does it is absent or null for everyone who was never
   * transferred.
   */
  transferredFrom: transferLinkSchema.nullable().optional(),
  /** Where this job went, when it ended as a transfer: the NEW employment. `pending` while the date has not arrived. */
  transferredTo: transferLinkSchema.nullable().optional(),
  /**
   * Org-defined extra fields (Organisation → Employee Fields), keyed by
   * EmployeeCustomFieldDefinition.key. Only fields this employee has a value for appear — a field
   * with no value stored is simply absent, not present as null, so a freshly-added field doesn't
   * turn every existing row's map into a wall of nulls.
   */
  customFields: customFieldValueMapSchema,
  /**
   * BUILT-IN fields not already a column above (Personal/Work/Bank Information — see
   * built-in-employee-fields.ts), so a company can pick "Personal Email" or "Date of Birth" as an
   * Employee List column the same way it picks a custom field. Present only on `list`, and only
   * for a row the caller may see PII for (`employee.pii:read`, or their own record) — same gate
   * `get()`'s `pii`/`bankAccount` use, because these ARE those same fields, just flattened for a
   * table cell instead of a detail-page section. Absent (not present with `null` values) for every
   * OTHER route this schema backs (create/update/exit/...), which never had a reason to compute it.
   */
  extendedFields: customFieldValueMapSchema.optional(),
});
export type Employee = z.infer<typeof employeeSchema>;

/**
 * A dependent as returned to a client — `aadhaarMasked` follows the same masking convention as
 * `employeePiiSchema.aadhaarMasked` below, never the decrypted value unmasked.
 */
export const dependentSchema = z.object({
  id: uuidSchema,
  relation: z.string(),
  name: z.string(),
  gender: z.string().nullable(),
  dateOfBirth: dateOnlySchema.nullable(),
  weddingDate: dateOnlySchema.nullable(),
  maritalStatus: z.string().nullable(),
  dateOfDeath: dateOnlySchema.nullable(),
  dateOfDivorce: dateOnlySchema.nullable(),
  physicalHandicap: z.boolean(),
  handicapPercentage: decimalStringSchema.nullable(),
  occupation: z.string().nullable(),
  mobileNumber: z.string().nullable(),
  aadhaarMasked: z.string().nullable(),
  companyInsurance: z.boolean(),
});
export type Dependent = z.infer<typeof dependentSchema>;

/** Work history prior to this employer — no encrypted fields, so nothing to mask. */
export const previousEmploymentSchema = z.object({
  id: uuidSchema,
  companyName: z.string(),
  category: z.string().nullable(),
  department: z.string().nullable(),
  subDepartment: z.string().nullable(),
  jobTitle: z.string().nullable(),
  location: z.string().nullable(),
  stateCode: z.string().nullable(),
  country: countrySchema.nullable(),
  startedOn: dateOnlySchema.nullable(),
  endedOn: dateOnlySchema.nullable(),
});
export type PreviousEmployment = z.infer<typeof previousEmploymentSchema>;

/**
 * PII fields, returned only when the caller holds `employee.pii:read` — see the route. A separate
 * schema rather than optional fields on `employeeSchema`, so a client's types make the gate
 * visible: code reading `employee.dob` simply does not compile against the directory shape.
 * `dependents` lives here, not on `employeeDetailSchema`, because a dependent carries the same
 * category of PII (Aadhaar) as the rest of this block — CLAUDE.md invariant 7.
 */
export const employeePiiSchema = z.object({
  dateOfBirth: dateOnlySchema.nullable(),
  gender: z.string().nullable(),
  personalEmail: emailSchema.nullable(),
  maritalStatus: z.string().nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactPhone: z.string().nullable(),
  emergencyContactRelation: z.string().nullable(),
  addressLine1: z.string().nullable(),
  city: z.string().nullable(),
  stateCode: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
  /** Present only when the ciphertext column is non-null, never the decrypted value unmasked. */
  panMasked: z.string().nullable(),
  aadhaarMasked: z.string().nullable(),
  dependents: z.array(dependentSchema),
});

/** Same masking posture as `employeePiiSchema` — `accountNumberMasked` only ever shows the last 4
 * digits, the full number is never decrypted into a response body. */
export const employeeBankAccountSchema = z.object({
  bankName: z.string().nullable(),
  branch: z.string().nullable(),
  bankCode1: z.string().nullable(),
  bankCode2: z.string().nullable(),
  accountNumberMasked: z.string().nullable(),
  /** The name on the account as the bank holds it; payment files use it over the legal name. */
  accountHolderName: z.string().nullable(),
});

export const employeeDetailSchema = employeeSchema.extend({
  pii: employeePiiSchema.nullable(),
  /** Not PII-gated — no encrypted field on this model, same reasoning as the schema comment above. */
  previousEmployments: z.array(previousEmploymentSchema),
  bankAccount: employeeBankAccountSchema.nullable(),
});
export type EmployeeDetail = z.infer<typeof employeeDetailSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const EMPLOYEE_LIST_SORTS = [
  'name',
  '-name',
  'joinedOn',
  '-joinedOn',
  'employeeCode',
  '-employeeCode',
] as const;
export type EmployeeListSort = (typeof EMPLOYEE_LIST_SORTS)[number];

export const listEmployeesQuerySchema = paginationQuerySchema.extend({
  /**
   * Matches employeeCode, firstName, lastName, email, or phone (by digits, any spelling) — a single
   * box, like the UI's search. A whole UUID word matches the person id exactly, which is how a
   * phone-only person (no email to search by) finds their own record.
   */
  q: z.string().trim().min(1).max(200).optional(),
  legalEntityId: legalEntityIdSchema.optional(),
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  shiftId: uuidSchema.optional(),
  status: employmentStatusSchema.optional(),
  /** Archived (deleted) employees are hidden unless this is 'true'. */
  archived: z.enum(['true', 'false']).optional(),
  /** List only a manager's reports — the shape "My Team" screens need without a second endpoint. */
  managerEmploymentId: uuidSchema.optional(),
  /** Joined on or after / on or before (inclusive). */
  joinedFrom: dateOnlySchema.optional(),
  joinedTo: dateOnlySchema.optional(),
  /**
   * "Due for confirmation": only people still on probation whose probation ends on or before this
   * date, overdue ones included.
   */
  probationEndBy: dateOnlySchema.optional(),
  /**
   * Sort order; `-` for descending. Absent keeps the stable creation order the cursor was built on.
   * Ties break on id, so a page boundary never repeats or skips a row.
   */
  sort: z.enum(EMPLOYEE_LIST_SORTS).optional(),
});

/**
 * Money as it arrives from the Add Employee wizard's Bank Information/compensation fields.
 * `currency` is required whenever an amount is — a bare number with no currency is exactly the
 * ambiguity `moneySchema` exists to prevent (CLAUDE.md invariant 2).
 */
export const compensationInputSchema = z
  .object({
    basicSalary: decimalStringSchema.optional(),
    salaryPerHour: decimalStringSchema.optional(),
    currency: currencySchema,
  })
  .refine((value) => value.basicSalary !== undefined || value.salaryPerHour !== undefined, {
    message: 'Provide at least one of basicSalary or salaryPerHour',
  });

/**
 * Bank Information, on the wire. `accountNumber` is plaintext here (TLS is the transport
 * protection) and is encrypted at rest with the person's DEK before it ever reaches a column —
 * see the crypto module and CLAUDE.md invariant 7. Never logged, never echoed back in a response.
 */
export const bankAccountInputSchema = z.object({
  bankName: z.string().trim().max(200).optional(),
  accountHolderName: z.string().trim().max(200).optional(),
  accountNumber: z
    .string()
    .trim()
    .regex(/^[0-9]{4,34}$/, 'Digits only, 4-34 characters')
    .optional(),
  branch: z.string().trim().max(200).optional(),
  accountType: z.enum(['savings', 'current']).optional(),
  bankCode1: z.string().trim().max(20).optional(),
  bankCode2: z.string().trim().max(20).optional(),
  addressLine1: z.string().trim().max(200).optional(),
  city: z.string().trim().max(100).optional(),
  stateCode: z.string().trim().max(10).optional(),
  country: countrySchema.optional(),
});

/** A dependent, on the wire. `aadhaar` is plaintext here for the same reason `accountNumber` above
 * is — encrypted at rest before it reaches a column, never logged, never echoed back unmasked. */
export const dependentInputSchema = z.object({
  relation: z.string().trim().min(1).max(100),
  name: z.string().trim().min(1).max(200),
  gender: genderSchema.optional(),
  dateOfBirth: dateOnlySchema.optional(),
  weddingDate: dateOnlySchema.optional(),
  maritalStatus: maritalStatusSchema.optional(),
  dateOfDeath: dateOnlySchema.optional(),
  dateOfDivorce: dateOnlySchema.optional(),
  physicalHandicap: z.boolean().optional(),
  handicapPercentage: decimalStringSchema.optional(),
  occupation: z.string().trim().max(200).optional(),
  mobileNumber: phoneSchema.optional(),
  aadhaar: z
    .string()
    .trim()
    .regex(/^[0-9]{12}$/, 'Aadhaar is 12 digits')
    .optional(),
  companyInsurance: z.boolean().optional(),
});

export const previousEmploymentInputSchema = z.object({
  companyName: z.string().trim().min(1).max(200),
  category: z.string().trim().max(100).optional(),
  department: z.string().trim().max(100).optional(),
  subDepartment: z.string().trim().max(100).optional(),
  jobTitle: z.string().trim().max(200).optional(),
  location: z.string().trim().max(200).optional(),
  stateCode: z.string().trim().max(10).optional(),
  country: countrySchema.optional(),
  startedOn: dateOnlySchema.optional(),
  endedOn: dateOnlySchema.optional(),
});

/**
 * The Add Employee fields, as an extendable object. The route validates `createEmployeeBodySchema`
 * below, which adds the email-or-phone rule.
 */
export const createEmployeeFieldsSchema = z.object({
  legalEntityId: legalEntityIdSchema,
  /**
   * The name, in either of two shapes (the handler requires one): `firstName` (+ `lastName`), as
   * Add Employee and a CSV with separate columns send it, kept exactly as typed; or one `name`, as
   * the Add-team grid presents it, split with `splitPersonName` (first word / remainder). A
   * one-word name leaves `lastName` empty. Not a refine: that would turn this object into an effect
   * that cannot be extended. Responses expose firstName and lastName separately.
   */
  firstName: z.string().trim().min(1).max(100).optional(),
  /** Empty or omitted for a person with no surname. */
  lastName: z.string().trim().max(100).optional(),
  name: z.string().trim().min(1).max(200).optional(),
  /**
   * The work email (login identity). Optional when `phone` is given: such a person is a PHONE-ONLY
   * employee who signs in with their mobile number and gets their invite by text, not email.
   * `createEmployeeBodySchema` refuses a body with neither (`requireEmailOrPhone`).
   */
  email: emailSchema.optional(),
  /**
   * Alternate login identity — presented at the same login field as email (see
   * loginRequestSchema's doc comment). Optional: left blank, the create route generates one from
   * the person's name. Always present in practice for the bulk importer, which sends the legacy
   * sheet's own username so migrated employees keep the identity they're used to.
   */
  username: z
    .string()
    .trim()
    .min(3)
    .max(64)
    .regex(/^[a-z0-9._-]+$/i, 'Letters, numbers, dot, underscore, and hyphen only')
    .optional(),
  /** E.164 mobile. Required when `email` is omitted — it is then the person's sign-in identity. */
  phone: phoneSchema.optional(),
  employmentType: employmentTypeSchema,
  joinedOn: dateOnlySchema,
  departmentId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  managerEmploymentId: uuidSchema.optional(),
  designation: z.string().max(200).optional(),
  grade: z.string().max(200).optional(),
  /** Catalogue grade for the initial assignment; its code is mirrored into `grade` (ADR 0022). */
  gradeId: uuidSchema.optional(),
  /** Catalogue cost centre for the initial assignment; must belong to this legal entity if scoped to one. */
  costCentreId: uuidSchema.optional(),
  /**
   * The role granted to this person on creation — `employee` (self-scoped) when omitted, so the
   * common case of onboarding a rank-and-file hire needs no extra input and no extra permission.
   * Requesting anything else requires `role:assign`/`org.settings:update` (checked in the route,
   * not here) — otherwise anyone who can create an employee could hand them `hr_admin`.
   */
  role: z.string().min(1).max(64).optional(),

  // --- Personal Information (Add Employee wizard, step 1) -------------------------------------
  personalEmail: emailSchema.optional(),
  /** A second, work-specific number distinct from `phone` — see the Person.workPhone comment. */
  workPhone: phoneSchema.optional(),
  gender: genderSchema.optional(),
  /** Encrypted at rest (CLAUDE.md invariant 7) — see the crypto module, never logged. */
  dateOfBirth: dateOnlySchema.optional(),
  addressLine1: z.string().trim().max(200).optional(),
  city: z.string().trim().max(100).optional(),
  stateCode: z.string().trim().max(10).optional(),
  postalCode: z.string().trim().max(20).optional(),
  country: countrySchema.optional(),
  qualification: z.string().trim().max(200).optional(),
  experience: z.string().trim().max(100).optional(),
  maritalStatus: maritalStatusSchema.optional(),
  childrenCount: z.number().int().min(0).max(20).optional(),
  emergencyContactName: z.string().trim().max(200).optional(),
  emergencyContactPhone: z.string().trim().max(30).optional(),
  emergencyContactRelation: z.string().trim().max(100).optional(),

  // --- Work Information (step 2) ---------------------------------------------------------------
  /** Omitted = no shift, unless `useDefaultShift` asks for the company default. */
  shiftId: uuidSchema.optional(),
  /**
   * With no `shiftId`: put the person on the company default shift (General 10:00-19:00, created on
   * first use). Sent by the first-run "Add your team" step only, so a company that already runs its
   * own shifts never has one made for it. Ignored when `shiftId` is sent.
   */
  useDefaultShift: z.literal(true).optional(),
  /** "Job Role" — see the EmploymentAssignment.jobRole schema comment for how it differs from
   * `designation` ("Job Position"). */
  jobRole: z.string().trim().max(200).optional(),
  workType: workTypeSchema.optional(),
  tags: z.array(z.string().trim().min(1).max(50)).max(20).optional(),
  /** Fixed-term contracts only — see Employment.contractEndOn. */
  contractEndOn: dateOnlySchema.optional(),
  /** Gated separately by `compensation:update` — see the create() route. */
  compensation: compensationInputSchema.optional(),

  // --- Bank Information (step 3) ---------------------------------------------------------------
  /** Gated separately by `compensation:update` — see the create() route. */
  bankAccount: bankAccountInputSchema.optional(),

  // --- Family and work history, no dedicated wizard step yet (see contract's header note) -------
  dependents: z.array(dependentInputSchema).max(20).optional(),
  previousEmployments: z.array(previousEmploymentInputSchema).max(20).optional(),

  /** Org-defined extra fields (Organisation → Employee Fields) — see employeeSchema.customFields. */
  customFields: customFieldValueMapSchema.optional(),

  /**
   * Email the new employee a one-time link to set their own password (`account.invite`, valid 7
   * days). Default `true`. There is no temporary password: with `false`, HR copies a sign-in link
   * from the employee's row actions when they want to hand it over themselves (WhatsApp, in
   * person). Following the link sets the password and verifies the email.
   */
  sendInvite: z.boolean().optional(),

  /**
   * Probation (Keka's model, UX-A7a). `probationPolicyId` picks a policy (absent = the org
   * default; `null` = no policy). `inProbation: false` means no probation at all.
   * `probationEndOn` overrides the policy's length. The starting status follows from these and
   * the joining date — see `employmentStart` in @hrms/shared.
   */
  probationPolicyId: uuidSchema.nullable().optional(),
  inProbation: z.boolean().optional(),
  probationEndOn: dateOnlySchema.optional(),

  /**
   * Add Employee's "Applications" step (ADR 0033): per connected application, whether to create
   * this person there (unticked = not now; HR can sync later) and HR's answers to its questions.
   * Absent = every applicable application with its default answers. Needs `app_integration:sync`;
   * without it the field is ignored and defaults apply.
   */
  applications: z
    .array(
      z.object({
        applicationId: uuidSchema,
        create: z.boolean().default(true),
        answers: z.record(z.string(), z.string().max(500)).default({}),
      }),
    )
    .max(20)
    .optional(),
});

/**
 * A new person needs SOME sign-in identity: a work email, or (phone-only employee) a mobile number.
 * Exported so a client that extends `createEmployeeFieldsSchema` applies the same rule.
 */
export function requireEmailOrPhone(
  value: { email?: string | undefined; phone?: string | undefined },
  ctx: z.RefinementCtx,
): void {
  if (value.email === undefined && value.phone === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['email'],
      message: 'Enter an email or a mobile number',
    });
  }
}

/** What the create route validates: the fields above, plus `requireEmailOrPhone`. */
export const createEmployeeBodySchema = createEmployeeFieldsSchema.superRefine(requireEmailOrPhone);
export type CreateEmployeeBody = z.infer<typeof createEmployeeBodySchema>;

/**
 * The create route's own response — `employeeDetailSchema` plus whether the invite email was
 * queued. No password is made or returned: the person sets their own through the invite link.
 */
export const createEmployeeResponseSchema = employeeDetailSchema.extend({
  /** An invite email was queued. Optional so this contract can land before the API that sets it. */
  inviteSent: z.boolean().optional(),
});

/**
 * Where an employee is in getting signed in:
 *   - `active`: they set a password (followed an invite) or verified at sign-up;
 *   - `invited`: a sign-in link is out and still valid;
 *   - `link_expired`: no password set yet and no valid link — resend or copy a new one.
 */
export const SIGN_IN_STATUSES = ['active', 'invited', 'link_expired'] as const;
export type SignInStatus = (typeof SIGN_IN_STATUSES)[number];

/** "Copy sign-in link" — the same one-time link, handed to HR instead of emailed. */
export const accountInviteLinkResponseSchema = z.object({
  /** One-time, valid for `expiresInDays`. Shown once; never log or store it. */
  inviteUrl: z.string(),
  /** The person's work email; null for a phone-only employee, who signs in with `phone`. */
  email: z.string().nullable(),
  /**
   * The person's E.164 mobile, when they have one — where Home's "Text your team their invite"
   * opens the SMS composer. Optional so this contract can land before the API that fills it.
   */
  phone: phoneSchema.nullable().optional(),
  expiresInDays: z.number().int(),
});
export type AccountInviteLinkResponse = z.infer<typeof accountInviteLinkResponseSchema>;

/** "Send sign-in link" — a first invite, a resend, or a reset; the email says which. */
export const sendAccountInviteResponseSchema = z.object({
  sent: z.literal(true),
  /**
   * Where it went — the employee's work email. A phone-only employee (no email) cannot be emailed:
   * the route answers 409 and the client copies the link (`getAccountInviteLink`) to text instead.
   */
  email: z.string(),
  expiresInDays: z.number().int(),
});
export type SendAccountInviteResponse = z.infer<typeof sendAccountInviteResponseSchema>;
export type CreateEmployeeResponse = z.infer<typeof createEmployeeResponseSchema>;

export const updateEmployeeBodySchema = z.object({
  firstName: z.string().trim().min(1).max(100).optional(),
  /** May be empty: a person with a one-word name has no surname. */
  lastName: z.string().trim().max(100).optional(),
  phone: phoneSchema.nullable().optional(),
  photoUrl: z.string().url().nullable().optional(),
  locale: localeSchema.optional(),

  // --- Personal information (HR's Edit Employee form). `null` clears a value; omitted = unchanged.
  personalEmail: emailSchema.nullable().optional(),
  workPhone: phoneSchema.nullable().optional(),
  gender: genderSchema.nullable().optional(),
  addressLine1: z.string().trim().max(200).nullable().optional(),
  city: z.string().trim().max(100).nullable().optional(),
  stateCode: z.string().trim().max(10).nullable().optional(),
  postalCode: z.string().trim().max(20).nullable().optional(),
  country: countrySchema.nullable().optional(),
  qualification: z.string().trim().max(200).nullable().optional(),
  experience: z.string().trim().max(100).nullable().optional(),
  maritalStatus: maritalStatusSchema.nullable().optional(),
  childrenCount: z.number().int().min(0).max(20).nullable().optional(),
  emergencyContactName: z.string().trim().max(200).nullable().optional(),
  emergencyContactPhone: z.string().trim().max(30).nullable().optional(),
  emergencyContactRelation: z.string().trim().max(100).nullable().optional(),

  // --- Sensitive: encrypted at rest (CLAUDE.md invariant 7), plaintext only on this request and
  // never echoed back. DOB and Aadhaar need `employee.pii:read`; PAN and the bank account need
  // `compensation:update` — checked in the handler, the same split profile-update approval uses.
  dateOfBirth: dateOnlySchema.nullable().optional(),
  aadhaar: z
    .string()
    .trim()
    .regex(/^[0-9]{12}$/, 'Aadhaar is 12 digits')
    .optional(),
  pan: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/, 'PAN is 5 letters, 4 digits, 1 letter')
    .optional(),
  /** Only the fields present are changed; a blank `accountNumber` keeps the one on file. */
  bankAccount: bankAccountInputSchema.optional(),

  /**
   * Org-defined extra fields (Organisation → Employee Fields) — see employeeSchema.customFields.
   * Only the keys present are changed; a key mapped to `null` clears that field's value.
   */
  customFields: customFieldValueMapSchema.optional(),
});

/**
 * A reassignment — department, location, manager, or designation change — is NEVER a field on
 * `updateEmployeeBodySchema`. It creates a new `EmploymentAssignment` row with `validFrom` today
 * and closes the current one, per CLAUDE.md invariant 5: history is additive, not overwritten.
 * "What was Priya's department in March" must stay a first-class query.
 */
export const reassignEmployeeBodySchema = z.object({
  departmentId: uuidSchema.nullable().optional(),
  locationId: uuidSchema.nullable().optional(),
  managerEmploymentId: uuidSchema.nullable().optional(),
  designation: z.string().max(200).nullable().optional(),
  grade: z.string().max(200).nullable().optional(),
  /**
   * Catalogue grade / cost centre (ADR 0022) — effective-dated with the rest of the new row. `null`
   * clears it; omitted carries the current value forward. Setting `gradeId` also sets `grade` to
   * the catalogue code, so send one or the other, not both.
   */
  gradeId: uuidSchema.nullable().optional(),
  costCentreId: uuidSchema.nullable().optional(),
  /** Business unit (ORG-6), same semantics as `costCentreId`: null clears, omitted carries forward. */
  businessUnitId: uuidSchema.nullable().optional(),
  shiftId: uuidSchema.nullable().optional(),
  jobRole: z.string().trim().max(200).nullable().optional(),
  workType: workTypeSchema.nullable().optional(),
  effectiveFrom: dateOnlySchema,
  reason: z.string().max(500).optional(),
});

/**
 * A quick employment-status change (e.g. probation -> confirmed, active -> on_leave), distinct
 * from `update()` (identity) and `reassign()` (org placement). `status` lives on `Employment`
 * itself, not `EmploymentAssignment` — it is a lifecycle flag on the immutable-facts half of the
 * model, so this is intentionally NOT effective-dated the way department/manager/designation are.
 */
export const updateEmploymentStatusBodySchema = z.object({
  status: employmentStatusSchema,
  reason: z.string().max(500).optional(),
});

/**
 * The two HR switches on the employee list. Both optional so one toggle sends only what it changed;
 * at least one is required so an empty PATCH is a 400, not a silent no-op.
 */
export const updateEmploymentFlagsBodySchema = z
  .object({
    isActive: z.boolean().optional(),
    autoAttendance: z.boolean().optional(),
  })
  .refine((value) => value.isActive !== undefined || value.autoAttendance !== undefined, {
    message: 'Provide isActive or autoAttendance',
  });

/**
 * Starts the exit/offboarding workflow — never a hard delete (Person/Employment have no delete
 * route at all; deletion of PII is crypto-shredding on a separate purge path). This route only
 * records the exit facts and kicks off approval via @hrms/workflow; `Employment.status` flips to
 * `exited` when that workflow resolves, not immediately here.
 */
export const exitEmployeeBodySchema = z.object({
  lastWorkingDay: dateOnlySchema,
  exitType: exitTypeSchema,
  exitReason: z.string().max(500).optional(),
});

// ---------------------------------------------------------------------------
// Profile Update Request DTOs
// ---------------------------------------------------------------------------

/**
 * One requested field change.
 *
 * SENSITIVE VALUES ARE NEVER STORED IN THE CLEAR HERE. `profile_update_requests.changes` is a
 * plain Json column, so whatever lands in `requestedValue` sits readable in the database for as
 * long as the request is pending — which for a PAN or a bank account number is exactly what
 * invariant 7 exists to prevent. For those fields the API replaces `requestedValue` with a MASK
 * and puts the ciphertext in `sealedValue`; approval decrypts that rather than reading the mask.
 *
 * The client never sets `sealedValue` and cannot opt a field out of being sealed: which fields
 * are sensitive is decided server-side from the field name, by the same matching the approval
 * handler already uses. A request that arrives with `sealedValue` set has it discarded.
 */
export const profileUpdateFieldChangeSchema = z.object({
  field: z.string(),
  /** Masked for sensitive fields — never the real current PAN or account number. */
  currentValue: z.string(),
  /** Masked for sensitive fields; the real value is in `sealedValue`. */
  requestedValue: z.string(),
  /**
   * AES-GCM ciphertext under the requesting person's DEK, set by the server for sensitive fields
   * only. Absent on every other field, and never populated from the request body.
   */
  sealedValue: z.string().optional(),
  /**
   * Stable identifier of the field (a `ProfileFieldKey`, or `family.*` / `education.*`), set by the
   * server. Approval reads this instead of guessing from the display label, which is how a field
   * called "Company" could once be mistaken for a PAN. Requests from before this existed have none
   * and are still matched by label. Never accepted from a client.
   */
  key: z.string().optional(),
  /**
   * The structured record behind a family/education change, for approval to apply. Server-set,
   * validated again when applied, and never accepted from a client.
   */
  payload: z.record(z.string(), z.unknown()).optional(),
});
export type ProfileUpdateFieldChange = z.infer<typeof profileUpdateFieldChangeSchema>;

export const profileUpdateAttachmentSchema = z.object({
  name: z.string(),
  size: z.string(),
  type: z.string(),
  url: z.string().optional(),
});

export const profileUpdateRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  employeeName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  category: z.string(),
  changes: z.array(profileUpdateFieldChangeSchema),
  employeeNote: z.string().nullable().optional(),
  attachments: z.array(profileUpdateAttachmentSchema).nullable().optional(),
  status: z.enum(['pending', 'approved', 'rejected']),
  reviewerNote: z.string().nullable().optional(),
  reviewedByPersonId: uuidSchema.nullable().optional(),
  reviewedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProfileUpdateRequest = z.infer<typeof profileUpdateRequestSchema>;

export const createProfileUpdateRequestBodySchema = z.object({
  category: z.enum(['Personal', 'Address', 'Bank Account', 'Tax & Statutory', 'Emergency Contact']),
  changes: z.array(profileUpdateFieldChangeSchema).min(1),
  employeeNote: z.string().trim().max(1000).optional(),
  attachments: z.array(profileUpdateAttachmentSchema).optional(),
});

/**
 * A person editing their OWN details. Each field is applied at once or held for HR according to
 * `PROFILE_FIELDS` in @hrms/shared — the same table the form reads to label its inputs. Job facts
 * (department, manager, grade, shift, pay) are deliberately absent: they are HR's, not self-editable.
 * Omit a field to leave it as it is.
 */
export const updateMyProfileBodySchema = z
  .object({
    phone: phoneSchema,
    personalEmail: emailSchema,
    gender: genderSchema,
    addressLine1: z.string().trim().min(1).max(200),
    city: z.string().trim().min(1).max(100),
    stateCode: z.string().trim().min(1).max(10),
    postalCode: z.string().trim().min(1).max(20),
    country: countrySchema,
    emergencyContactName: z.string().trim().min(1).max(200),
    emergencyContactPhone: z.string().trim().min(1).max(30),
    emergencyContactRelation: z.string().trim().min(1).max(100),
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().min(1).max(100),
    maritalStatus: maritalStatusSchema,
    dateOfBirth: dateOnlySchema,
    aadhaar: z
      .string()
      .trim()
      .regex(/^[0-9]{12}$/, 'Aadhaar is 12 digits'),
    pan: z
      .string()
      .trim()
      .regex(/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/, 'PAN is 5 letters, 4 digits, 1 letter'),
    bankName: z.string().trim().min(1).max(200),
    branch: z.string().trim().min(1).max(200),
    bankCode1: z.string().trim().min(1).max(20),
    accountNumber: z
      .string()
      .trim()
      .regex(/^[0-9]{4,34}$/, 'Account number is 4-34 digits'),
  })
  .partial()
  .extend({
    employeeNote: z.string().trim().max(1000).optional(),
    /**
     * Org-defined custom fields the caller may edit (`selfAccess: 'edit'`), keyed by field key.
     * Each is applied at once or held for HR by its own `selfEditApproval`. Values cross the wire
     * as strings, validated against the field's type like on Add/Edit Employee. Clearing a value
     * isn't offered here — ask HR.
     */
    customFields: z.record(z.string(), z.string().trim().min(1).max(500)).optional(),
  })
  .refine(
    (value) =>
      Object.entries(value).some(
        ([key, v]) => key !== 'employeeNote' && key !== 'customFields' && v !== undefined,
      ) || Object.keys(value.customFields ?? {}).length > 0,
    { message: 'Change at least one field' },
  );

export const updateMyProfileResponseSchema = z.object({
  /** Fields that were saved immediately. */
  applied: z.array(z.enum(PROFILE_FIELD_KEYS)),
  /** Custom field keys saved immediately; the rest of `customFields` is in a pending request. */
  appliedCustomFields: z.array(z.string()),
  /** One pending request per category, for the fields that need HR's approval. */
  pending: z.array(profileUpdateRequestSchema),
});
export type UpdateMyProfileResponse = z.infer<typeof updateMyProfileResponseSchema>;

export const listProfileUpdateRequestsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
  status: z.enum(['all', 'pending', 'approved', 'rejected']).optional(),
  category: z.string().optional(),
});

export const rejectProfileUpdateRequestBodySchema = z.object({
  reviewerNote: z.string().trim().min(1, 'Rejection reason is required').max(1000),
});

export const approveProfileUpdateRequestBodySchema = z.object({
  reviewerNote: z.string().trim().max(1000).optional(),
});

// ---------------------------------------------------------------------------
// Compensation Schemas
// ---------------------------------------------------------------------------

export const compensationItemSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  employeeName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  basicSalary: z.string().nullable().optional(),
  salaryPerHour: z.string().nullable().optional(),
  currency: z.string(),
  validFrom: z.string(),
  validTo: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  status: z.enum(['active', 'historic']),
  createdAt: z.string(),
});
export type CompensationItem = z.infer<typeof compensationItemSchema>;

export const listCompensationQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
  status: z.enum(['all', 'active', 'historic']).optional(),
});

export const addCompensationBodySchema = z.object({
  // Decimal strings, per CLAUDE.md invariant 2 — never a JS number for money. Matches
  // `compensationInputSchema` above, which this route should have used from the start.
  basicSalary: decimalStringSchema.optional(),
  salaryPerHour: decimalStringSchema.optional(),
  currency: z.string().length(3).default('INR'),
  validFrom: dateOnlySchema,
  reason: z.string().trim().max(500).optional(),
});

// ---------------------------------------------------------------------------
// Resignation Request Schemas
// ---------------------------------------------------------------------------

export const resignationRequestSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  employeeName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  reasonCategory: z.string(),
  reasonDetails: z.string(),
  requestedLastWorkingDay: z.string(),
  approvedLastWorkingDay: z.string().nullable().optional(),
  noticePeriodDays: z.number(),
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED', 'REVOKE_REQUESTED', 'REVOKED']),
  revokeReason: z.string().nullable().optional(),
  rejectionNote: z.string().nullable().optional(),
  reviewerNote: z.string().nullable().optional(),
  submittedAt: z.string(),
  reviewedAt: z.string().nullable().optional(),
  reviewedByPersonId: uuidSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ResignationRequestDto = z.infer<typeof resignationRequestSchema>;

export const submitResignationBodySchema = z.object({
  reasonCategory: z.enum([
    'Better Opportunity',
    'Personal/Family',
    'Career Change',
    'Higher Education',
    'Relocation',
    'Other',
  ]),
  reasonDetails: z
    .string()
    .trim()
    .min(5, 'Please provide more detail for your resignation')
    .max(2000),
  requestedLastWorkingDay: dateOnlySchema,
  noticePeriodDays: z.number().int().min(0).max(365).default(30),
});

export const listResignationRequestsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
  status: z
    .enum(['all', 'PENDING', 'APPROVED', 'REJECTED', 'REVOKE_REQUESTED', 'REVOKED'])
    .optional(),
});

export const approveResignationBodySchema = z.object({
  approvedLastWorkingDay: dateOnlySchema.optional(),
  reviewerNote: z.string().trim().max(1000).optional(),
});

export const rejectResignationBodySchema = z.object({
  rejectionNote: z.string().trim().min(1, 'Rejection note is required').max(1000),
});

export const revokeResignationBodySchema = z.object({
  revokeReason: z.string().trim().min(5, 'Reason for revocation is required').max(1000),
});

export const approveRevokeBodySchema = z.object({
  reviewerNote: z.string().trim().max(1000).optional(),
});

export const rejectRevokeBodySchema = z.object({
  rejectionNote: z.string().trim().min(1, 'Rejection note is required').max(1000),
});

// ---------------------------------------------------------------------------
// Full & Final Settlement Schemas
//
// Manually entered by HR/Finance, not computed — packages/payroll's statutory engine does not
// exist yet, so there is nothing to calculate gratuity or leave encashment from (see ROADMAP.md).
// Approval goes through @hrms/workflow, unlike resignation/profile-update-request above: this is
// real money leaving the company, closer to a payout than anything else in this module.
// ---------------------------------------------------------------------------

/**
 * Every individual settlement line is a credit or a debit, never itself negative — the SIGN is
 * expressed by which field it's in (a credit field vs. the separate `deductions` debit list), not
 * by the number. `decimalStringSchema` alone allows a leading `-` (money can legitimately be
 * negative elsewhere, e.g. a payroll adjustment), so this narrows it for the one place a negative
 * value is never valid input. The computed NET payable is deliberately NOT constrained this way —
 * it can go negative when deductions exceed credits, which is a real scenario (the company
 * recovering money from the employee), not a bug.
 */
const nonNegativeDecimalStringSchema = decimalStringSchema.refine(
  (value) => Number(value) >= 0,
  'Must be zero or greater',
);

export const fnfDeductionLineSchema = z.object({
  label: z.string().trim().min(1).max(200),
  amount: nonNegativeDecimalStringSchema,
});
export type FnfDeductionLine = z.infer<typeof fnfDeductionLineSchema>;

export const fnfSettlementSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  employmentId: uuidSchema,
  employeeCode: z.string(),
  employeeName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  departmentName: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  resignationRequestId: uuidSchema.nullable().optional(),
  currency: z.string(),
  pendingSalaryAmount: z.string().nullable().optional(),
  leaveEncashmentDays: z.string().nullable().optional(),
  leaveEncashmentAmount: z.string().nullable().optional(),
  gratuityAmount: z.string().nullable().optional(),
  bonusAmount: z.string().nullable().optional(),
  deductions: z.array(fnfDeductionLineSchema).nullable().optional(),
  /** Credits minus debits above — computed server-side, never trusted from the client. */
  netPayableAmount: z.string(),
  status: z.enum(['pending_approval', 'approved', 'rejected', 'paid']),
  preparedByPersonId: uuidSchema,
  reviewerNote: z.string().nullable().optional(),
  settledOn: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FnfSettlementDto = z.infer<typeof fnfSettlementSchema>;

export const createFnfSettlementBodySchema = z
  .object({
    employmentId: uuidSchema,
    resignationRequestId: uuidSchema.optional(),
    currency: currencySchema,
    pendingSalaryAmount: nonNegativeDecimalStringSchema.optional(),
    leaveEncashmentDays: nonNegativeDecimalStringSchema.optional(),
    leaveEncashmentAmount: nonNegativeDecimalStringSchema.optional(),
    gratuityAmount: nonNegativeDecimalStringSchema.optional(),
    bonusAmount: nonNegativeDecimalStringSchema.optional(),
    deductions: z.array(fnfDeductionLineSchema).max(20).optional(),
  })
  .refine(
    (value) =>
      value.pendingSalaryAmount !== undefined ||
      value.leaveEncashmentAmount !== undefined ||
      value.gratuityAmount !== undefined ||
      value.bonusAmount !== undefined ||
      (value.deductions?.length ?? 0) > 0,
    { message: 'Provide at least one settlement component' },
  );

export const listFnfSettlementsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
  status: z.enum(['all', 'pending_approval', 'approved', 'rejected', 'paid']).optional(),
});

export const decideFnfSettlementBodySchema = z.object({
  comment: z.string().trim().max(1000).optional(),
});

export const markFnfSettlementPaidBodySchema = z.object({
  settledOn: dateOnlySchema,
});

/**
 * The decrypted identifiers behind the masked values everywhere else, for the employee export only.
 * Plaintext on the wire (TLS), never logged, never cached, and the route is hidden from the AI
 * copilot. Anyone calling it needs `employee.pii:export`, and each call is audited (count only).
 */
export const EXPORT_SENSITIVE_MAX_IDS = 100;
export const exportSensitiveBodySchema = z.object({
  employmentIds: z.array(uuidSchema).min(1).max(EXPORT_SENSITIVE_MAX_IDS),
});
export const exportSensitiveItemSchema = z.object({
  employmentId: uuidSchema,
  pan: z.string().nullable(),
  aadhaar: z.string().nullable(),
  bankAccountNumber: z.string().nullable(),
});
export type ExportSensitiveItem = z.infer<typeof exportSensitiveItemSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const employeeContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      query: listEmployeesQuerySchema,
      responses: {
        200: paginatedSchema(employeeSchema),
        401: errorBodySchema,
      },
      summary: 'List employees in the caller’s visible scope',
      metadata: routeMeta(null),
    },

    exportSensitive: {
      method: 'POST',
      path: '/export/sensitive',
      body: exportSensitiveBodySchema,
      responses: {
        200: z.object({ items: z.array(exportSensitiveItemSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Decrypted PAN, Aadhaar and bank account number for up to 100 employees, for the employee export',
      // Decrypted PII must never reach the model (ADR 0009); the export screen is the only caller.
      metadata: routeMeta('employee.pii:export', { ai: 'hidden' }),
    },

    createProfileUpdateRequest: {
      method: 'POST',
      path: '/profile-requests',
      body: createProfileUpdateRequestBodySchema,
      responses: {
        201: profileUpdateRequestSchema,
        401: errorBodySchema,
        400: errorBodySchema,
      },
      summary: 'Submit a profile update request',
      metadata: routeMeta(null),
    },

    addMyEmployment: {
      method: 'POST',
      path: '/me/employment',
      body: addMyEmploymentBodySchema,
      responses: {
        201: z.object({ employmentId: uuidSchema, employeeCode: z.string() }),
        401: errorBodySchema,
        // PLAN_LIMIT_REACHED: the free-trial employee cap (see employees.create).
        402: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Add yourself as an employee: a job for your own login (the owner after sign-up), never a second person',
      metadata: routeMeta('employee:create'),
    },

    updateMyProfile: {
      method: 'PATCH',
      path: '/me/profile',
      body: updateMyProfileBodySchema,
      responses: {
        200: updateMyProfileResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Change your own details — contact info applies now, identity/tax/bank changes go to HR',
      metadata: routeMeta(null),
    },

    getMyProfileUpdateRequests: {
      method: 'GET',
      path: '/profile-requests/my',
      responses: {
        200: z.array(profileUpdateRequestSchema),
        401: errorBodySchema,
      },
      summary: 'Get caller’s submitted profile update requests',
      metadata: routeMeta(null),
    },

    listProfileUpdateRequests: {
      method: 'GET',
      path: '/profile-requests',
      query: listProfileUpdateRequestsQuerySchema,
      responses: {
        200: paginatedSchema(profileUpdateRequestSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List all profile update requests for HR review',
      metadata: routeMeta('employee:update'),
    },

    approveProfileUpdateRequest: {
      method: 'POST',
      path: '/profile-requests/:requestId/approve',
      body: approveProfileUpdateRequestBodySchema,
      responses: {
        200: profileUpdateRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Approve a profile update request and apply changes to database',
      // `employee.pii:read` applies only when the request changes a date of birth or Aadhaar
      // (checked in the handler); listed so the declaration covers every permission it names.
      metadata: routeMeta(['compensation:update', 'employee:update', 'employee.pii:read']),
    },

    rejectProfileUpdateRequest: {
      method: 'POST',
      path: '/profile-requests/:requestId/reject',
      body: rejectProfileUpdateRequestBodySchema,
      responses: {
        200: profileUpdateRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Reject a profile update request with mandatory reason',
      metadata: routeMeta('employee:update'),
    },

    // ---------------------------------------------------------------------------
    // Compensation Routes
    // ---------------------------------------------------------------------------

    listCompensation: {
      method: 'GET',
      path: '/compensation/list',
      query: listCompensationQuerySchema,
      responses: {
        200: paginatedSchema(compensationItemSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List org-wide employee compensation items',
      metadata: routeMeta('compensation:read', { salaryPin: 'others' }),
    },

    getCompensationHistory: {
      method: 'GET',
      path: '/compensation/history/:employmentId',
      responses: {
        200: z.array(compensationItemSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get full compensation history for an employee',
      // Any-of: one's own pay (`compensation.own:read`, every employee) or someone else's in scope
      // (`compensation:read`). The handler decides which applies to this employment (ADR 0023).
      metadata: routeMeta(['compensation:read', 'compensation.own:read'], { salaryPin: 'subject' }),
    },

    addCompensation: {
      method: 'POST',
      path: '/compensation/:employmentId',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: addCompensationBodySchema,
      responses: {
        201: compensationItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Add a new effective-dated compensation revision',
      metadata: routeMeta('compensation:update', { salaryPin: 'others' }),
    },

    // ---------------------------------------------------------------------------
    // Resignation Routes
    // ---------------------------------------------------------------------------

    submitResignation: {
      method: 'POST',
      path: '/resignations',
      body: submitResignationBodySchema,
      responses: {
        201: resignationRequestSchema,
        401: errorBodySchema,
        400: errorBodySchema,
      },
      summary: 'Submit a resignation request (Employee Self-Service)',
      metadata: routeMeta(null),
    },

    getMyResignationRequests: {
      method: 'GET',
      path: '/resignations/my',
      responses: {
        200: z.array(resignationRequestSchema),
        401: errorBodySchema,
      },
      summary: 'Get caller’s resignation request status and history',
      metadata: routeMeta(null),
    },

    listResignationRequests: {
      method: 'GET',
      path: '/resignations',
      query: listResignationRequestsQuerySchema,
      responses: {
        200: paginatedSchema(resignationRequestSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List all resignation requests for HR review',
      metadata: routeMeta('offboarding:read'),
    },

    approveResignation: {
      method: 'POST',
      path: '/resignations/:requestId/approve',
      body: approveResignationBodySchema,
      responses: {
        200: resignationRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Approve a resignation request and update employment exit status',
      metadata: routeMeta('offboarding:manage'),
    },

    rejectResignation: {
      method: 'POST',
      path: '/resignations/:requestId/reject',
      body: rejectResignationBodySchema,
      responses: {
        200: resignationRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Reject a resignation request',
      metadata: routeMeta('offboarding:manage'),
    },

    revokeResignation: {
      method: 'POST',
      path: '/resignations/:requestId/revoke',
      body: revokeResignationBodySchema,
      responses: {
        200: resignationRequestSchema,
        401: errorBodySchema,
        400: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Employee requests revocation of submitted/approved resignation',
      metadata: routeMeta(null),
    },

    approveRevokeResignation: {
      method: 'POST',
      path: '/resignations/:requestId/approve-revoke',
      body: approveRevokeBodySchema,
      responses: {
        200: resignationRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'HR approves revocation request and restores employment active status',
      metadata: routeMeta('offboarding:manage'),
    },

    rejectRevokeResignation: {
      method: 'POST',
      path: '/resignations/:requestId/reject-revoke',
      body: rejectRevokeBodySchema,
      responses: {
        200: resignationRequestSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'HR rejects revocation request',
      metadata: routeMeta('offboarding:manage'),
    },

    // ---------------------------------------------------------------------------
    // Full & Final Settlement Routes
    // ---------------------------------------------------------------------------

    createFnfSettlement: {
      method: 'POST',
      path: '/fnf-settlements',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: createFnfSettlementBodySchema,
      responses: {
        201: fnfSettlementSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Record a Full & Final settlement for an exiting employee and start its approval',
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },

    getMyFnfSettlements: {
      method: 'GET',
      path: '/fnf-settlements/my',
      responses: {
        200: z.array(fnfSettlementSchema),
        401: errorBodySchema,
      },
      summary: 'Get caller’s own Full & Final settlement records',
      metadata: routeMeta(null),
    },

    listFnfSettlements: {
      method: 'GET',
      path: '/fnf-settlements',
      query: listFnfSettlementsQuerySchema,
      responses: {
        200: paginatedSchema(fnfSettlementSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List Full & Final settlements for HR/Finance review',
      metadata: routeMeta('payroll:read', { salaryPin: 'others' }),
    },

    approveFnfSettlement: {
      method: 'POST',
      path: '/fnf-settlements/:settlementId/approve',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: decideFnfSettlementBodySchema,
      responses: {
        200: fnfSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Approve a Full & Final settlement through its @hrms/workflow approval step',
      metadata: routeMeta(null, { salaryPin: 'others' }),
    },

    rejectFnfSettlement: {
      method: 'POST',
      path: '/fnf-settlements/:settlementId/reject',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: decideFnfSettlementBodySchema,
      responses: {
        200: fnfSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Reject a Full & Final settlement through its @hrms/workflow approval step',
      metadata: routeMeta(null, { salaryPin: 'others' }),
    },

    markFnfSettlementPaid: {
      method: 'POST',
      path: '/fnf-settlements/:settlementId/mark-paid',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: markFnfSettlementPaidBodySchema,
      responses: {
        200: fnfSettlementSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Record that an approved settlement has actually been paid out',
      metadata: routeMeta('fnf:execute', { salaryPin: 'others' }),
    },

    get: {
      method: 'GET',
      path: '/:employmentId',
      responses: {
        200: employeeDetailSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one employee. `pii` is null unless the caller holds employee.pii:read.',
      metadata: routeMeta('employee:read'),
    },

    create: {
      method: 'POST',
      path: '/',
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: createEmployeeBodySchema,
      responses: {
        201: createEmployeeResponseSchema,
        401: errorBodySchema,
        // PLAN_LIMIT_REACHED: a free-trial org already has FREE_TRIAL_EMPLOYEE_LIMIT employees
        // besides the owner. `details` carries `{ limit, used }`.
        402: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a person, their first employment, and its initial assignment',
      // `role:assign` / `org.settings:update` apply only when the body requests a non-default `role`
      // (checked in the handler, alongside the unconditional `employee:create`); listed here so the
      // declaration covers every permission the handler names. The handler still enforces
      // `employee:create` itself, so this does not widen who can create an employee.
      metadata: routeMeta(['employee:create', 'role:assign', 'org.settings:update']),
    },

    // HR's way to get someone signed in: a one-time set-password link by email (7 days). Used for
    // a first invite that went astray, a resend, and an admin-initiated password reset alike —
    // the person chooses the password; HR never sees or sets one. Older links still expire.
    sendAccountInvite: {
      method: 'POST',
      path: '/:employmentId/invite',
      body: z.object({}).optional(),
      responses: {
        200: sendAccountInviteResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Email an employee a one-time link to set their password (invite, resend or reset)',
      metadata: routeMeta('employee:update'),
    },

    // For people with no email access (shop floor, drivers) and for password resets for them: HR
    // gets the one-time link and passes it on. HR never sees or sets a password, but whoever holds
    // the link can set one — so it is audited, refused for your own account, refused for a Global
    // Admin unless you are one, and each new link cancels the older ones.
    getAccountInviteLink: {
      method: 'POST',
      path: '/:employmentId/invite-link',
      body: z.object({}).optional(),
      responses: {
        200: accountInviteLinkResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Get a one-time sign-in link to hand over yourself, instead of emailing it',
      metadata: routeMeta('employee:update'),
    },

    update: {
      method: 'PATCH',
      path: '/:employmentId',
      body: updateEmployeeBodySchema,
      responses: {
        200: employeeDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update identity fields only — never department, manager, or designation',
      metadata: routeMeta('employee:update'),
    },

    reassign: {
      method: 'POST',
      path: '/:employmentId/reassign',
      // The API mounts `idempotency()` on this route, so a request without the header is a 400.
      // A double-submitted reassign would otherwise close and reopen the assignment twice.
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: reassignEmployeeBodySchema,
      responses: {
        200: employeeDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        412: errorBodySchema,
      },
      summary: 'Effective-dated reassignment — closes the current assignment, opens a new one',
      metadata: routeMeta('employment:update'),
    },

    updateStatus: {
      method: 'PATCH',
      path: '/:employmentId/status',
      body: updateEmploymentStatusBodySchema,
      responses: {
        200: employeeDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        // Also the probation gate: probation -> confirmed is refused with the same
        // `incomplete_required_tasks` / `extension_pending` details as `confirmProbation`.
        409: errorBodySchema,
      },
      summary: 'Quick employment-status change — distinct from update() and reassign()',
      metadata: routeMeta('employment:update'),
    },

    getProbation: {
      method: 'GET',
      path: '/:employmentId/probation',
      responses: {
        200: probationDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Probation panel data — summary, confirmation blockers, linkable probation reviews',
      metadata: routeMeta('employment:update'),
    },

    confirmProbation: {
      method: 'POST',
      path: '/:employmentId/confirm-probation',
      body: confirmProbationBodySchema,
      responses: {
        200: probationSummarySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Confirm employment after probation — 409 with details.reason (parseProbationConflict) when blocked',
      metadata: routeMeta('employment:update'),
    },

    extendProbation: {
      method: 'POST',
      path: '/:employmentId/extend-probation',
      body: extendProbationBodySchema,
      responses: {
        200: extendProbationResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Request a probation extension (approval workflow) — 409 with details.reason when not on probation, at the cap, or one is already pending',
      metadata: routeMeta('employment:update'),
    },

    listProbationExtensions: {
      method: 'GET',
      path: '/:employmentId/probation-extensions',
      responses: {
        200: probationExtensionListSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Probation extension history for one employment, newest first',
      metadata: routeMeta('employment:update'),
    },

    cancelProbationExtension: {
      method: 'POST',
      path: '/:employmentId/probation-extensions/:extensionId/cancel',
      body: c.noBody(),
      responses: {
        200: probationExtensionSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Withdraw a pending extension request — 409 extension_not_pending once decided',
      metadata: routeMeta('employment:update'),
    },

    updateFlags: {
      method: 'PATCH',
      path: '/:employmentId/flags',
      body: updateEmploymentFlagsBodySchema,
      responses: {
        200: employeeDetailSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Turn an employee’s active flag or automatic attendance on or off',
      metadata: routeMeta('employment:update'),
    },

    archive: {
      method: 'POST',
      path: '/:employmentId/archive',
      body: z.object({}),
      responses: {
        200: employeeDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Delete an employee from the list — hides them, keeps every record',
      metadata: routeMeta('employment.exit:execute'),
    },

    restore: {
      method: 'POST',
      path: '/:employmentId/restore',
      body: z.object({}),
      responses: {
        200: employeeDetailSchema,
        401: errorBodySchema,
        // PLAN_LIMIT_REACHED: the free-trial employee cap (see employees.create).
        402: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Bring an archived employee back onto the list',
      metadata: routeMeta('employment.exit:execute'),
    },

    exit: {
      method: 'POST',
      path: '/:employmentId/exit',
      body: exitEmployeeBodySchema,
      responses: {
        200: employeeDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Start the exit/offboarding workflow — never a hard delete',
      metadata: routeMeta('employment.exit:execute'),
    },

    previewTransfer: {
      method: 'GET',
      path: '/:employmentId/transfer-preview',
      query: previewTransferQuerySchema,
      responses: {
        200: transferPreviewSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'What a transfer to another legal entity would do: reports, headships, roles, leave types, open items, payroll periods, and anything that would refuse it',
      metadata: routeMeta('employment.transfer:execute', { salaryPin: 'others' }),
    },

    transfer: {
      method: 'POST',
      path: '/:employmentId/transfer',
      // The API mounts `idempotency()` on this route: a retried request returns the first result,
      // so a double click can never create two employments.
      headers: z.object({ 'idempotency-key': idempotencyKeySchema }),
      body: transferEmployeeBodySchema,
      responses: {
        201: transferResultSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        // 409: the state refuses it (same company, payroll finalized, exit open, a transfer pending,
        // code taken ...). 422: the choices do not fit (salary missing, open items not reviewed,
        // placement from another company). `details.reason` names which, see TRANSFER_BLOCKER_CODES.
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Move a person to another legal entity: ends this employment the day before and starts a new one, keeping one person and one login',
      metadata: routeMeta('employment.transfer:execute'),
    },

    cancelTransfer: {
      method: 'POST',
      // `:employmentId` is EITHER employment of the pending transfer (the old record HR is looking
      // at, or the new one waiting as pre_joining).
      path: '/:employmentId/transfer/cancel',
      body: cancelTransferBodySchema,
      responses: {
        200: employeeDetailSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Cancel a transfer that has not taken effect yet: removes the new employment and restores the old one',
      metadata: routeMeta('employment.transfer:execute'),
    },
  },
  { pathPrefix: '/employees' },
);
